import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Banknote,
  Building2,
  ChevronDown,
  ChevronUp,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle,
  Clock,
  Eye,
  EyeOff,
  IndianRupee,
  Minus,
  Plus,
  RefreshCw,
  Save,
  TrendingUp,
  Upload,
  Wallet,
  X,
} from 'lucide-react';
import { Technician } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

// ── helpers ────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (s: string) =>
  new Date(s).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
  });

// ── Collapsible section wrapper ────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  icon: React.ReactNode;
  badge?: string;
  badgeColor?: string;
  children: React.ReactNode;
}> = ({ title, icon, badge, badgeColor = 'bg-gray-100 text-gray-600', children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="text-gray-500">{icon}</div>
          <span className="text-sm font-bold text-gray-900">{title}</span>
          {badge && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badgeColor}`}>
              {badge}
            </span>
          )}
        </div>
        {open
          ? <ChevronUp className="h-4 w-4 text-gray-400" />
          : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {open && <div className="border-t border-gray-100 p-5">{children}</div>}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

const EditTechnicianPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const technicianId = Number(id);

  // ── Technician form ──────────────────────────────────────────────────────
  const [isLoading, setIsLoading]     = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [technician, setTechnician]   = useState<Technician | null>(null);
  const [name, setName]               = useState('');
  const [mobile, setMobile]           = useState('');
  const [email, setEmail]             = useState('');
  const [specialization, setSpecialization] = useState('');
  const [experience, setExperience]   = useState('');
  const [status, setStatus]           = useState('Active');
  const [photo, setPhoto]             = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState('');

  // ── Wallet state ─────────────────────────────────────────────────────────
  const [wallet, setWallet]           = useState<any>(null);
  const [bank, setBank]               = useState<any>(null);
  const [txns, setTxns]               = useState<any[]>([]);
  const [txPage, setTxPage]           = useState(1);
  const [hasMore, setHasMore]         = useState(true);
  const [txLoading, setTxLoading]     = useState(false);

  // Bank account form
  const [bankForm, setBankForm] = useState({
    accountHolderName: '', accountNumber: '', confirmAccountNumber: '', ifsc: '', bankName: '',
  });
  const [showAcct, setShowAcct]               = useState(false);
  const [showConfirmAcct, setShowConfirmAcct] = useState(false);
  const [savingBank, setSavingBank]           = useState(false);

  // Manual adjustment
  const [adjust, setAdjust]     = useState({ amount: '', type: 'CREDIT' as 'CREDIT' | 'DEBIT', notes: '' });
  const [adjusting, setAdjusting] = useState(false);

  // ── Load technician ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!id) return;
    const init = async () => {
      setIsLoading(true);
      try {
        const response = await apiService.getTechnician(technicianId);
        if (response.isSuccess && response.entity) {
          const tech = response.entity;
          setTechnician(tech);
          setName(tech.name);
          setMobile(tech.mobile);
          setEmail(tech.email);
          setSpecialization(tech.specialization);
          setExperience(tech.experience.toString());
          setStatus(tech.status);
          if (tech.imagePath) setPhotoPreview(tech.imagePath);
        } else {
          toast.error(response.errors?.join(', ') || 'Failed to load technician');
          navigate('/technicians');
        }
      } catch {
        toast.error('Failed to load technician');
        navigate('/technicians');
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [id]);

  // ── Load wallet data ─────────────────────────────────────────────────────

  const loadWallet = useCallback(async () => {
    try { setWallet(await apiService.getAdminWallet(technicianId)); } catch { setWallet(null); }
  }, [technicianId]);

  const loadBank = useCallback(async () => {
    try { setBank(await apiService.getAdminBankAccount(technicianId)); }
    catch (e: any) { if (e?.response?.status === 404) setBank(null); }
  }, [technicianId]);

  const loadTxns = useCallback(async (page: number) => {
    setTxLoading(true);
    try {
      const items = await apiService.getAdminWalletTransactions(technicianId, page, 20);
      if (page === 1) setTxns(items ?? []);
      else setTxns(prev => [...prev, ...(items ?? [])]);
      setHasMore((items ?? []).length === 20);
    } catch { /* silent */ }
    finally { setTxLoading(false); }
  }, [technicianId]);

  useEffect(() => {
    if (!technicianId) return;
    Promise.all([loadWallet(), loadBank(), loadTxns(1)]);
  }, [loadWallet, loadBank, loadTxns]);

  // ── Form handlers ────────────────────────────────────────────────────────

  const handlePhotoUpload = (file: File) => {
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Please select PNG, JPEG or JPG'); return;
    }
    if (file.size > 2 * 1024 * 1024) { toast.error('File must be < 2MB'); return; }
    const reader = new FileReader();
    reader.onload = (e) => { setPhoto(file); setPhotoPreview(e.target?.result as string); };
    reader.readAsDataURL(file);
  };

  const handleUpdateTechnician = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!technician) return;
    if (!name.trim() || !mobile.trim() || !email.trim() || !specialization.trim()) {
      toast.error('All fields are required.'); return;
    }
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('mobile', mobile);
      fd.append('email', email);
      fd.append('specialization', specialization);
      fd.append('experience', experience);
      fd.append('status', status);
      if (photo) fd.append('photo', photo);
      const response = await apiService.updateTechnician(technician.id, fd);
      if (response.isSuccess) {
        toast.success('Technician updated successfully');
        navigate('/technicians');
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to update technician');
      }
    } catch { toast.error('Error updating technician'); }
    finally { setIsSubmitting(false); }
  };

  const handleSaveBank = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bankForm.accountHolderName.trim() || !bankForm.accountNumber.trim() || !bankForm.ifsc.trim()) {
      toast.error('Account holder name, account number and IFSC are required.'); return;
    }
    if (bankForm.accountNumber !== bankForm.confirmAccountNumber) {
      toast.error('Account numbers do not match.'); return;
    }
    setSavingBank(true);
    try {
      await apiService.saveAdminBankAccount(technicianId, {
        accountHolderName: bankForm.accountHolderName.trim(),
        accountNumber: bankForm.accountNumber.trim(),
        ifsc: bankForm.ifsc.trim().toUpperCase(),
        bankName: bankForm.bankName.trim(),
      });
      toast.success('Bank account saved.');
      setBankForm({ accountHolderName: '', accountNumber: '', confirmAccountNumber: '', ifsc: '', bankName: '' });
      await loadBank();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save bank account.');
    } finally { setSavingBank(false); }
  };

  const handleAdjust = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(adjust.amount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount.'); return; }
    if (!adjust.notes.trim()) { toast.error('Reason is required.'); return; }
    setAdjusting(true);
    try {
      const res = await apiService.adjustAdminWallet(technicianId, { amount, type: adjust.type, notes: adjust.notes.trim() });
      toast.success(res.message || 'Wallet adjusted.');
      setAdjust({ amount: '', type: 'CREDIT', notes: '' });
      await loadWallet();
      await loadTxns(1);
      setTxPage(1);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to adjust wallet.');
    } finally { setAdjusting(false); }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!technician) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Technician not found</h3>
        <button onClick={() => navigate('/technicians')} className="text-purple-600 hover:text-purple-700">
          Back to Technicians
        </button>
      </div>
    );
  }

  const totalWallet = (wallet?.availableBalance ?? 0) + (wallet?.pendingBalance ?? 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/technicians')}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Technicians
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Edit Technician</h1>
            <p className="text-sm text-gray-500">{technician.name} · ID {technicianId}</p>
          </div>
        </div>
        <button
          onClick={async () => { await Promise.all([loadWallet(), loadBank(), loadTxns(1)]); setTxPage(1); }}
          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh wallet
        </button>
      </div>

      {/* ── WALLET SECTIONS (collapsible, collapsed by default) ── */}

      {/* 1. Wallet Balance */}
      <Section
        title="Wallet Balance"
        icon={<Wallet className="h-5 w-5" />}
        badge={fmt(totalWallet)}
        badgeColor="bg-indigo-50 text-indigo-700"
      >
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Total Wallet', value: fmt(totalWallet), icon: <Wallet className="h-4 w-4 text-indigo-600" />, bg: 'bg-indigo-50', color: 'text-indigo-700' },
            { label: 'Available (Withdrawable)', value: fmt(wallet?.availableBalance ?? 0), icon: <IndianRupee className="h-4 w-4 text-green-600" />, bg: 'bg-green-50', color: 'text-green-700' },
            { label: 'Pending (T+2)', value: fmt(wallet?.pendingBalance ?? 0), icon: <Clock className="h-4 w-4 text-yellow-600" />, bg: 'bg-yellow-50', color: 'text-yellow-700' },
            { label: 'Total Withdrawn', value: fmt(wallet?.totalWithdrawn ?? 0), icon: <Banknote className="h-4 w-4 text-blue-600" />, bg: 'bg-blue-50', color: 'text-blue-700' },
          ].map(c => (
            <div key={c.label} className="bg-gray-50 rounded-lg border border-gray-100 p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-1.5 rounded-md ${c.bg}`}>{c.icon}</div>
                <span className="text-xs font-medium text-gray-500">{c.label}</span>
              </div>
              <div className={`text-xl font-black ${c.color}`}>{c.value}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 2. Bank Account */}
      <Section
        title="Bank Account"
        icon={<Building2 className="h-5 w-5" />}
        badge={bank ? 'On file' : 'Not added'}
        badgeColor={bank ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}
      >
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Current */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Current Account</p>
            {bank ? (
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Account Holder', value: bank.accountHolderName },
                  { label: 'Bank Name', value: bank.bankName || '-' },
                  { label: 'Account No.', value: bank.accountNumber },
                  { label: 'IFSC', value: bank.ifsc },
                ].map(f => (
                  <div key={f.label} className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-400 mb-0.5">{f.label}</div>
                    <div className="text-sm font-semibold text-gray-900">{f.value || '-'}</div>
                  </div>
                ))}
                {bank.isVerified && (
                  <div className="col-span-2 flex items-center gap-2 text-green-700 text-xs font-semibold">
                    <CheckCircle className="h-4 w-4" /> Verified
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-lg bg-red-50 border border-red-100 p-4 text-sm text-red-700">
                No bank account added yet.
              </div>
            )}
          </div>

          {/* Add / Update form */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
              {bank ? 'Update Account' : 'Add Account'}
            </p>
            <form onSubmit={handleSaveBank} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Account Holder Name *</label>
                <input
                  type="text"
                  value={bankForm.accountHolderName}
                  onChange={e => setBankForm(f => ({ ...f, accountHolderName: e.target.value }))}
                  placeholder="e.g. Rahul Sharma"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Account Number *</label>
                <div className="relative">
                  <input
                    type={showAcct ? 'text' : 'password'}
                    inputMode="numeric"
                    value={bankForm.accountNumber}
                    onChange={e => setBankForm(f => ({ ...f, accountNumber: e.target.value.replace(/\D/g, '') }))}
                    placeholder="Enter account number"
                    autoComplete="off"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                  <button type="button" onClick={() => setShowAcct(v => !v)} tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showAcct ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Re-enter Account Number *</label>
                <div className="relative">
                  <input
                    type={showConfirmAcct ? 'text' : 'password'}
                    inputMode="numeric"
                    value={bankForm.confirmAccountNumber}
                    onChange={e => setBankForm(f => ({ ...f, confirmAccountNumber: e.target.value.replace(/\D/g, '') }))}
                    placeholder="Re-enter account number"
                    autoComplete="off"
                    className={`w-full border rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 ${
                      bankForm.confirmAccountNumber.length > 0
                        ? bankForm.accountNumber === bankForm.confirmAccountNumber
                          ? 'border-green-400 focus:ring-green-300'
                          : 'border-red-400 focus:ring-red-300'
                        : 'border-gray-200 focus:ring-indigo-400'
                    }`}
                  />
                  <button type="button" onClick={() => setShowConfirmAcct(v => !v)} tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showConfirmAcct ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {bankForm.confirmAccountNumber.length > 0 && (
                  <p className={`text-xs mt-1 font-medium ${bankForm.accountNumber === bankForm.confirmAccountNumber ? 'text-green-600' : 'text-red-500'}`}>
                    {bankForm.accountNumber === bankForm.confirmAccountNumber ? '✓ Account numbers match' : '✗ Account numbers do not match'}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">IFSC Code *</label>
                  <input
                    type="text"
                    value={bankForm.ifsc}
                    onChange={e => setBankForm(f => ({ ...f, ifsc: e.target.value.toUpperCase() }))}
                    placeholder="e.g. HDFC0001234"
                    maxLength={11}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={bankForm.bankName}
                    onChange={e => setBankForm(f => ({ ...f, bankName: e.target.value }))}
                    placeholder="e.g. HDFC Bank"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
              </div>
              <button
                type="submit" disabled={savingBank}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingBank
                  ? <><RefreshCw className="h-4 w-4 animate-spin" /> Saving...</>
                  : <><Save className="h-4 w-4" /> {bank ? 'Update' : 'Save'} Bank Account</>}
              </button>
            </form>
          </div>
        </div>
      </Section>

      {/* 3. Adjust Wallet */}
      <Section title="Adjust Wallet Balance" icon={<IndianRupee className="h-5 w-5" />}>
        <form onSubmit={handleAdjust} className="space-y-3">
          <div className="flex gap-2">
            <button type="button" onClick={() => setAdjust(a => ({ ...a, type: 'CREDIT' }))}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
                adjust.type === 'CREDIT' ? 'bg-green-50 border-green-400 text-green-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}>
              <Plus className="h-4 w-4" /> Add Money
            </button>
            <button type="button" onClick={() => setAdjust(a => ({ ...a, type: 'DEBIT' }))}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
                adjust.type === 'DEBIT' ? 'bg-red-50 border-red-400 text-red-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}>
              <Minus className="h-4 w-4" /> Deduct Money
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹) *</label>
              <input
                type="number" min="1" step="0.01"
                value={adjust.amount}
                onChange={e => setAdjust(a => ({ ...a, amount: e.target.value }))}
                placeholder="e.g. 500"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Reason *</label>
              <input
                type="text"
                value={adjust.notes}
                onChange={e => setAdjust(a => ({ ...a, notes: e.target.value }))}
                placeholder="e.g. Bonus for December"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
          </div>
          <button type="submit" disabled={adjusting}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg disabled:opacity-50 ${
              adjust.type === 'CREDIT' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
            }`}>
            {adjusting
              ? <><RefreshCw className="h-4 w-4 animate-spin" /> Processing...</>
              : adjust.type === 'CREDIT'
                ? <><Plus className="h-4 w-4" /> Credit ₹{adjust.amount || '0'} to Wallet</>
                : <><Minus className="h-4 w-4" /> Deduct ₹{adjust.amount || '0'} from Wallet</>}
          </button>
        </form>
      </Section>

      {/* 4. Transaction History */}
      <Section
        title="Transaction History"
        icon={<TrendingUp className="h-5 w-5" />}
        badge={txns.length > 0 ? `${txns.length}+` : undefined}
      >
        {txns.length === 0 && !txLoading ? (
          <div className="text-center py-8 text-gray-400 text-sm">No transactions yet.</div>
        ) : (
          <div className="divide-y divide-gray-50 -mx-5">
            {txns.map(tx => {
              const isCredit = tx.type?.toLowerCase() === 'credit';
              return (
                <div key={tx.id} className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isCredit ? 'bg-green-50' : 'bg-blue-50'}`}>
                      {isCredit
                        ? <ArrowDownLeft className="h-4 w-4 text-green-600" />
                        : <ArrowUpRight className="h-4 w-4 text-blue-600" />}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {tx.description || tx.notes || (isCredit ? 'Credit' : 'Withdrawal')}
                      </div>
                      <div className="text-xs text-gray-400">{fmtDate(tx.createdAt)}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-bold ${isCredit ? 'text-green-600' : 'text-blue-600'}`}>
                      {isCredit ? '+' : '-'}{fmt(tx.amount)}
                    </div>
                    <div className="text-xs text-gray-400 uppercase">{tx.status}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {txLoading && <div className="text-center py-4 text-gray-400 text-sm">Loading...</div>}
        {hasMore && !txLoading && txns.length > 0 && (
          <button
            onClick={() => { const next = txPage + 1; setTxPage(next); loadTxns(next); }}
            className="w-full flex items-center justify-center gap-2 py-3 text-sm text-gray-500 hover:text-gray-800 font-medium border-t border-gray-100 mt-3"
          >
            <ChevronDown className="h-4 w-4" /> Load more
          </button>
        )}
      </Section>

      {/* ── EDIT TECHNICIAN FORM ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-bold text-gray-900">Technician Details</h2>
        </div>
        <form onSubmit={handleUpdateTechnician} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Enter full name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
                <input type="tel" value={mobile} onChange={e => setMobile(e.target.value)} placeholder="Enter mobile number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Specialization *</label>
                <input type="text" value={specialization} onChange={e => setSpecialization(e.target.value)} placeholder="e.g. Mobile Repair"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience *</label>
                <input type="number" min="0" max="50" value={experience} onChange={e => setExperience(e.target.value)} placeholder="Years"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select value={status} onChange={e => setStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="On Leave">On Leave</option>
                </select>
              </div>
            </div>
          </div>

          {/* Photo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo</label>
            {photoPreview ? (
              <div className="relative inline-block">
                <img src={photoPreview} alt="Preview" className="h-32 w-32 rounded-lg object-cover border border-gray-200" />
                <button type="button" onClick={() => { setPhoto(null); setPhotoPreview(''); }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                <Upload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                <label htmlFor="photo-upload" className="cursor-pointer text-sm text-purple-600 hover:text-purple-700 font-medium">
                  Click to upload
                </label>
                <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB</p>
                <input id="photo-upload" type="file" accept="image/*" className="hidden"
                  onChange={e => e.target.files?.[0] && handlePhotoUpload(e.target.files[0])} />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => navigate('/technicians')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting}
              className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg hover:from-purple-700 hover:to-purple-800 disabled:opacity-50">
              {isSubmitting
                ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> Updating...</>
                : <><Save className="h-4 w-4 mr-2" /> Update Technician</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTechnicianPage;
